import { TrackingEvent } from '@marketplace-web/shared/event-tracker-data'

import {
  SelectedDynamicFilterModel,
  FilterModel,
  SortByOption,
} from '@marketplace-web/domain/catalog-data'

type endOfListEventArgs = {
  itemCount: number
  page?: number
  perPage?: number
}

type EndOfListEventExtra = {
  item_count: number
  page?: number
  per_page?: number
}

export const endOfListEvent = (args: endOfListEventArgs): TrackingEvent => {
  const { itemCount, page, perPage } = args

  const extra: EndOfListEventExtra = {
    item_count: itemCount,
  }

  if (page) extra.page = page
  if (perPage) extra.per_page = perPage

  return {
    event: 'user.reach_end_of_list',
    extra,
  }
}

const nullifyEmptyValue = <T>(value: T): T | null =>
  !value ||
  (Array.isArray(value) && (value as Array<unknown>).length === 0) ||
  Object.keys(value).length === 0
    ? null
    : value

type UserFilterItemsEventArgs = {
  itemCount: number
  filters: FilterModel
  searchQuery: string | null
  searchCorrelationId: string
  searchSessionId: string
  globalSearchSessionId: string | null
  globalCatalogBrowseSessionId?: string | null
  searchStartId: string | null
  searchStartType: string | null
  selectedDynamicFilters: Array<SelectedDynamicFilterModel>
}

type UserFilterItemsEventExtra = {
  item_count: number
  search_correlation_id: string
  search_session_id: string
  global_search_session_id: string | null
  global_catalog_browse_session_id?: string | null
  conditions: Partial<{
    search_term: string | null
    brand_ids: Array<number> | null
    catalog_ids: Array<number> | null
    color_ids: Array<number> | null
    material_ids: Array<number> | null
    video_game_rating_ids: Array<number> | null
    size_ids: Array<number> | null
    status_ids: Array<number> | null
    price_from: number | null
    price_to: number | null
    order: string | null
    popular: boolean
  }>
  search_start_id?: string
  search_start_type?: string
  selected_filters?: Record<string, Array<number>>
}

export const userFilterItemsEvent = (args: UserFilterItemsEventArgs): TrackingEvent => {
  const {
    itemCount,
    filters,
    searchQuery,
    searchCorrelationId,
    searchSessionId,
    globalSearchSessionId,
    globalCatalogBrowseSessionId,
    searchStartId,
    searchStartType,
    selectedDynamicFilters,
  } = args

  const extra: UserFilterItemsEventExtra = {
    item_count: itemCount,
    search_correlation_id: searchCorrelationId,
    search_session_id: searchSessionId,
    global_search_session_id: globalSearchSessionId,
    global_catalog_browse_session_id: globalCatalogBrowseSessionId || undefined,
    conditions: {
      search_term: searchQuery,
      catalog_ids: nullifyEmptyValue(filters.catalogIds),
      price_from: filters.priceFrom ? parseFloat(filters.priceFrom) : null,
      price_to: filters.priceTo ? parseFloat(filters.priceTo) : null,
      order: filters.sortBy ? nullifyEmptyValue(filters.sortBy) : SortByOption.Relevance,
      popular: filters.isPopularCatalog,
    },
  }

  if (searchStartType) {
    extra.search_start_type = searchStartType
  }

  if (searchStartId) {
    extra.search_start_id = searchStartId
  }

  selectedDynamicFilters
    .filter(({ ids }) => ids.length)
    .forEach(({ type, ids }) => {
      if (!extra.selected_filters) extra.selected_filters = {}

      extra.selected_filters[type] = ids
      // TODO: remove this when dynamic catalog filters scaled
      extra.conditions[`${type}_ids`] = ids
    })

  return {
    event: 'user.filter_items',
    extra,
  }
}

type UserPaginateCatalogEventArgs = {
  currentPage: number
  searchCorrelationId: string
  searchSessionId: string
  globalSearchSessionId: string | null
  globalCatalogBrowseSessionId?: string | null
}

type UserPaginateCatalogEventExtra = {
  page: number
  search_correlation_id?: string
  search_session_id?: string
  global_search_session_id?: string
  global_catalog_browse_session_id?: string | null
}

export const userPaginateCatalogEvent = (args: UserPaginateCatalogEventArgs): TrackingEvent => {
  const {
    currentPage,
    searchCorrelationId,
    searchSessionId,
    globalSearchSessionId,
    globalCatalogBrowseSessionId,
  } = args

  const extra: UserPaginateCatalogEventExtra = {
    page: currentPage,
  }

  if (searchCorrelationId) extra.search_correlation_id = searchCorrelationId
  if (searchSessionId) extra.search_session_id = searchSessionId
  if (globalSearchSessionId) extra.global_search_session_id = globalSearchSessionId
  if (globalCatalogBrowseSessionId)
    extra.global_catalog_browse_session_id = globalCatalogBrowseSessionId

  return {
    event: 'user.paginate_catalog',
    extra,
  }
}

type UserAppliedOrderEventArgs = {
  order: SortByOption
  searchSessionId: string
  globalSearchSessionId: string | null
}

type UserAppliedOrderEventExtra = {
  order: string
  search_session_id?: string
  global_search_session_id?: string
}

export const userAppliedOrderEvent = (args: UserAppliedOrderEventArgs) => {
  const { order, searchSessionId, globalSearchSessionId } = args

  const extra: UserAppliedOrderEventExtra = {
    order,
  }

  if (searchSessionId) extra.search_session_id = searchSessionId

  if (globalSearchSessionId) extra.global_search_session_id = globalSearchSessionId

  return { event: 'user.applied_order', extra }
}

type ClickCatalogBackArgs = {
  searchSessionId: string | null
  globalSearchSessionId: string | null
  globalCatalogBrowseSessionId: string | null
}

type ClickCatalogBackExtra = {
  search_session_id: string | null
  global_search_session_id: string | null
  global_catalog_browse_session_id: string | null
}
export const clickCatalogBack = (args: ClickCatalogBackArgs) => {
  const extra: ClickCatalogBackExtra = {
    search_session_id: args.searchSessionId,
    global_search_session_id: args.globalSearchSessionId,
    global_catalog_browse_session_id: args.globalCatalogBrowseSessionId,
  }

  return {
    event: 'user.click_catalog_back',
    extra,
  }
}
